import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFlow, emails, sendBroadcast as sendBroadcastApi } from '@/lib/api/email-flows';
import type { FlowStatus } from '@/lib/api/email-flows/types';
import { QUERY_KEYS, stanNotify } from '@/lib';
import { formatTimeForAPI } from '@/lib/helpers';
import { combineTags } from '@/features/email-flows/logic/audienceBuilder.logic';
import { BROADCAST_DELAY, BROADCAST_FLOW_CONFIG } from '../constants';
import type { Tag } from '@/features/email-flows/types/audience.types';
import type { BroadcastFormData } from '../validations/broadcastForm';

interface SaveBroadcastParams {
    flowId: number;
    emailId?: number;
    data: BroadcastFormData;
    status: FlowStatus;
    schedule?: {
        selectedDate: Date;
        selectedTime: number;
        selectedTimezone: string;
    };
}

interface SendBroadcastParams {
    flowId: number;
    emailId: number;
    tags: Tag[];
}

export function useBroadcastMutations() {
    const queryClient = useQueryClient();

    // Save broadcast (flow + email)
    const saveBroadcast = useMutation({
        mutationFn: async ({ flowId, emailId, data, status, schedule }: SaveBroadcastParams) => {
            // Create or update flow
            const flowData: any = {
                status,
                type: BROADCAST_FLOW_CONFIG.TYPE,
                trigger: BROADCAST_FLOW_CONFIG.TRIGGER,
                data: {
                    name: BROADCAST_FLOW_CONFIG.NAME,
                    specific_time: {
                        tags: combineTags(data.tags, data.excludedTags),
                    },
                },
            };

            // Only add schedule data when explicitly scheduling (status === 'published' AND schedule provided)
            if (schedule) {
                const year = schedule.selectedDate.getFullYear();
                const month = String(schedule.selectedDate.getMonth() + 1).padStart(2, '0');
                const day = String(schedule.selectedDate.getDate()).padStart(2, '0');
                const formattedDate = `${year}-${month}-${day}`;
                const formattedTime = formatTimeForAPI(schedule.selectedTime);
                const timezone = schedule.selectedTimezone;

                flowData.data.specific_time.date = formattedDate;
                flowData.data.specific_time.time = formattedTime;
                flowData.data.specific_time.timezone = timezone;
            } else {
                const date = new Date(); // Get the current date
                date.setDate(date.getDate() + 2);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0'); // Adding 1 as months are zero-based
                const day = String(date.getDate()).padStart(2, '0');
                const formattedDate = `${year}-${month}-${day}`;

                flowData.data.specific_time.date = formattedDate;
                flowData.data.specific_time.time = formatTimeForAPI(((date.getHours() + 1) % 24) * 60);
                flowData.data.specific_time.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            }

            // Only add flow_id if editing existing flow
            if (flowId > 0) {
                flowData.flow_id = flowId;
            }

            const flowResponse = await createFlow(flowData);
            const newFlowId = flowResponse.flow_id;

            // Create or update email
            const emailPayload: any = {
                flow_id: newFlowId,
                status,
                data: {
                    subject: data.subject,
                    body: data.body,
                    delay: BROADCAST_DELAY.SECONDS,
                    delay_unit: BROADCAST_DELAY.UNIT,
                },
            };

            // Only add flow_email_id if editing existing email
            if (emailId && emailId > 0) {
                emailPayload.flow_email_id = emailId;
            }

            const emailResponse = await emails.upsertFlowEmail(emailPayload);

            return { flowId: newFlowId, emailId: emailResponse.flow_email_id };
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BROADCASTS] });
            if (variables.flowId > 0) {
                queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FLOW_DETAILS, variables.flowId] });
            }
        },
        onError: (error: any) => {
            console.error('Error saving broadcast:', error);
            // Only show error notification for actual API failures (4xx/5xx status codes)
            // This prevents false errors when broadcasts are successfully scheduled
            if (error?.response?.status) {
                stanNotify({
                    title: 'Error',
                    text: 'Failed to save broadcast',
                    type: 'error',
                });
            }
        },
    });

    // Send broadcast immediately
    const sendBroadcast = useMutation({
        mutationFn: async ({ flowId, emailId, tags }: SendBroadcastParams) => {
            await sendBroadcastApi({
                flow_id: flowId,
                flow_email_id: emailId,
                tags,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BROADCASTS] });
        },
        onError: error => {
            console.error('Error sending broadcast:', error);
            stanNotify({
                title: 'Error',
                text: 'Failed to send broadcast',
                type: 'error',
            });
        },
    });

    // Send test email
    const sendTestEmail = useMutation({
        mutationFn: async ({
            emailAddress,
            subject,
            body,
        }: {
            emailAddress: string;
            subject: string;
            body: string;
        }) => {
            await emails.sendTestEmail({
                data: { subject, body },
                email_address: emailAddress,
            });
        },
        onSuccess: () => {
            stanNotify({
                title: 'Success',
                text: 'Test email sent successfully',
                type: 'success',
            });
        },
        onError: error => {
            console.error('Test send failed:', error);
            stanNotify({
                title: 'Error',
                text: 'Failed to send test email',
                type: 'error',
            });
        },
    });

    return {
        saveBroadcast,
        sendBroadcast,
        sendTestEmail,
    };
}
